import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, Avatar } from '@components/ui';
import { useThemeStore } from '@store';
import { useUpdateProjectMember } from '@hooks';
import { userService } from '@services';

// Roles disponibles
const PROJECT_ROLES = [
  { value: 'OWNER', label: 'Propietario', icon: '👑', color: '#92400E' },
  { value: 'ADMIN', label: 'Administrador', icon: '⚙️', color: '#6D28D9' },
  { value: 'MEMBER', label: 'Miembro', icon: '👤', color: '#065F46' },
  { value: 'VIEWER', label: 'Observador', icon: '👁️', color: '#475569' },
];

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export default function EditMemberModal({
  isOpen,
  onClose,
  member,
  projectId,
  onSuccess,
}) {
  const [selectedRole, setSelectedRole] = useState('MEMBER');
  const [userName, setUserName] = useState('');
  const [showNameSection, setShowNameSection] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState(false);
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';

  const updateMember = useUpdateProjectMember();

  const user = member?.user || {};

  useEffect(() => {
    if (isOpen && member) {
      setSelectedRole(member.role || 'MEMBER');
      setUserName(user.name || '');
      setShowNameSection(false);
      setError('');
      setSuccessMessage('');
    }
  }, [isOpen, member, user.name]);

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || '?';
  };

  const handleUpdateRole = async () => {
    if (selectedRole === member.role) {
      setError('El rol es el mismo');
      return;
    }

    setIsUpdatingRole(true);
    setError('');
    setSuccessMessage('');

    try {
      await updateMember.mutateAsync({
        projectId,
        userId: member.userId || user.id,
        data: { role: selectedRole },
      });
      setSuccessMessage('Rol actualizado exitosamente');
      setTimeout(() => {
        onSuccess?.();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar rol');
    } finally {
      setIsUpdatingRole(false);
    }
  };

  const handleUpdateName = async () => {
    if (!userName || userName.trim().length < 2) {
      setError('El nombre debe tener al menos 2 caracteres');
      return;
    }

    if (userName.trim() === user.name) {
      setError('El nombre es el mismo');
      return;
    }

    setIsUpdatingName(true);
    setError('');
    setSuccessMessage('');

    try {
      await userService.update(member.userId || user.id, { name: userName.trim() });
      setSuccessMessage('Nombre actualizado exitosamente');
      setShowNameSection(false);
      setTimeout(() => {
        onSuccess?.();
      }, 1000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al actualizar nombre');
    } finally {
      setIsUpdatingName(false);
    }
  };

  // Estilos
  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '600',
    color: isDark ? '#94A3B8' : '#64748B',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.02em',
  };

  const roleGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
  };

  const roleOptionStyle = (isSelected, roleColor) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '14px 16px',
    borderRadius: '12px',
    border: `2px solid ${isSelected ? roleColor : (isDark ? '#334155' : '#E2E8F0')}`,
    backgroundColor: isSelected
      ? (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)')
      : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  });

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: isDark ? '#1E293B' : '#F8FAFC',
    border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
    borderRadius: '12px',
    fontSize: '15px',
    color: isDark ? '#F1F5F9' : '#1E293B',
    outline: 'none',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  };

  const errorStyle = {
    padding: '12px 16px',
    backgroundColor: isDark ? 'rgba(239, 68, 68, 0.1)' : '#FEF2F2',
    border: `1px solid ${isDark ? 'rgba(239, 68, 68, 0.3)' : '#FECACA'}`,
    borderRadius: '12px',
    color: '#EF4444',
    fontSize: '14px',
  };

  const successStyle = {
    padding: '12px 16px',
    backgroundColor: isDark ? 'rgba(16, 185, 129, 0.1)' : '#ECFDF5',
    border: `1px solid ${isDark ? 'rgba(16, 185, 129, 0.3)' : '#A7F3D0'}`,
    borderRadius: '12px',
    color: '#10B981',
    fontSize: '14px',
  };

  const sectionStyle = {
    padding: '20px',
    backgroundColor: isDark ? 'rgba(30, 41, 59, 0.5)' : '#F8FAFC',
    borderRadius: '12px',
    border: `1px solid ${isDark ? '#334155' : '#E2E8F0'}`,
  };

  if (!member) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Editar Miembro"
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        {/* User Info */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '16px',
          backgroundColor: isDark ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
          borderRadius: '12px',
          border: `1px solid ${isDark ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.2)'}`,
        }}>
          <Avatar initials={getInitials(user.name)} size="lg" />
          <div>
            <div style={{
              fontSize: '18px',
              fontWeight: '600',
              color: isDark ? '#F1F5F9' : '#1E293B',
            }}>
              {user.name}
            </div>
            <div style={{
              fontSize: '14px',
              color: isDark ? '#94A3B8' : '#64748B',
            }}>
              {user.email}
            </div>
          </div>
        </div>

        {/* Messages */}
        {error && <div style={errorStyle}>{error}</div>}
        {successMessage && <div style={successStyle}>{successMessage}</div>}

        {/* Role Section */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Rol en el Proyecto</label>
          <div style={roleGridStyle}>
            {PROJECT_ROLES.map((role) => {
              const isSelected = selectedRole === role.value;
              return (
                <div
                  key={role.value}
                  onClick={() => setSelectedRole(role.value)}
                  style={roleOptionStyle(isSelected, role.color)}
                  onMouseEnter={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = isDark ? '#475569' : '#CBD5E1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) {
                      e.currentTarget.style.borderColor = isDark ? '#334155' : '#E2E8F0';
                    }
                  }}
                >
                  <span style={{ fontSize: '20px' }}>{role.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: isSelected ? role.color : (isDark ? '#F1F5F9' : '#1E293B'),
                    }}>
                      {role.label}
                    </div>
                  </div>
                  {isSelected && (
                    <span style={{ color: role.color }}>
                      <CheckIcon />
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: '16px' }}>
            <Button
              variant="primary"
              onClick={handleUpdateRole}
              loading={isUpdatingRole}
              disabled={selectedRole === member.role}
              style={{ width: '100%' }}
            >
              Actualizar Rol
            </Button>
          </div>
        </div>

        {/* Name Section */}
        <div style={sectionStyle}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: showNameSection ? '16px' : '0',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <UserIcon />
              <span style={{
                fontSize: '14px',
                fontWeight: '600',
                color: isDark ? '#F1F5F9' : '#1E293B',
              }}>
                Nombre
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowNameSection(!showNameSection)}
            >
              {showNameSection ? 'Cancelar' : 'Editar'}
            </Button>
          </div>

          {showNameSection && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                placeholder="Nombre del usuario"
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#6366F1';
                  e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = isDark ? '#334155' : '#E2E8F0';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <Button
                variant="primary"
                onClick={handleUpdateName}
                loading={isUpdatingName}
                disabled={!userName || userName.trim().length < 2 || userName.trim() === user.name}
                style={{ width: '100%' }}
              >
                Guardar Nombre
              </Button>
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}

EditMemberModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  member: PropTypes.object,
  projectId: PropTypes.number,
  onSuccess: PropTypes.func,
};
